/**
 * @fileoverview useScreenScale hook for responsive screen preview scaling
 * @module screen-view/hooks/useScreenScale
 *
 * Uses ResizeObserver to auto-calculate scale factor for fitting
 * a 430×932 screen (iPhone 14 Pro) into any container using "contain" logic.
 *
 * Features:
 * - ResizeObserver for real-time container monitoring
 * - Debounced updates to prevent layout thrashing
 * - GPU-accelerated via CSS transform recommendation
 * - Configurable padding, min/max scale bounds
 */

import { useEffect, useState, useRef, useCallback } from 'react';
import type { RefObject } from 'react';
import { SCREEN_DESIGN_SIZE } from '../constants/designSize';

/**
 * Configuration options for useScreenScale hook
 */
export interface UseScreenScaleOptions {
  /** Padding inside container in pixels (default: 0) */
  padding?: number;
  /** Minimum scale factor (default: 0.1) */
  minScale?: number;
  /** Maximum scale factor (default: 1.0) */
  maxScale?: number;
  /** Debounce delay for resize events in ms (default: 16 for ~60fps) */
  debounce?: number;
  /** Content width to scale (default: SCREEN_DESIGN_SIZE.WIDTH = 430) */
  contentWidth?: number;
  /** Content height to scale (default: SCREEN_DESIGN_SIZE.HEIGHT = 932) */
  contentHeight?: number;
}

/**
 * Result returned by useScreenScale hook
 */
export interface UseScreenScaleResult {
  /** Calculated scale factor to apply via CSS transform */
  scale: number;
  /** Current container width in pixels */
  containerWidth: number;
  /** Current container height in pixels */
  containerHeight: number;
  /** Whether the container has been measured and scale is ready */
  isReady: boolean;
  /** Scaled content width (SCREEN_DESIGN_SIZE.WIDTH * scale) */
  scaledWidth: number;
  /** Scaled content height (SCREEN_DESIGN_SIZE.HEIGHT * scale) */
  scaledHeight: number;
}

/**
 * useScreenScale - Auto-calculates scale factor for responsive screen preview
 *
 * Uses ResizeObserver to monitor container size and calculates the scale
 * factor needed to fit a 430×932 screen using "contain" mode.
 *
 * @param containerRef - Ref to the container element
 * @param options - Configuration options
 * @returns Scale factor and container dimensions
 *
 * @example
 * ```tsx
 * const containerRef = useRef<HTMLDivElement>(null);
 * const { scale, isReady } = useScreenScale(containerRef, { padding: 16 });
 *
 * return (
 *   <div ref={containerRef} style={{ width: '100%', height: '100%' }}>
 *     <div style={{
 *       width: 430,
 *       height: 932,
 *       transform: `scale(${scale})`,
 *       transformOrigin: 'top center',
 *       visibility: isReady ? 'visible' : 'hidden',
 *     }}>
 *       <ScreenView screen={screen} locale="en" />
 *     </div>
 *   </div>
 * );
 * ```
 */
export function useScreenScale(
  containerRef: RefObject<HTMLElement | null>,
  options: UseScreenScaleOptions = {}
): UseScreenScaleResult {
  const {
    padding = 0,
    minScale = 0.1,
    maxScale = 1.0,
    debounce = 16, // ~60fps
    contentWidth,
    contentHeight,
  } = options;

  // Use provided dimensions or fall back to default SCREEN_DESIGN_SIZE
  const targetWidth = contentWidth ?? SCREEN_DESIGN_SIZE.WIDTH;
  const targetHeight = contentHeight ?? SCREEN_DESIGN_SIZE.HEIGHT;

  const [result, setResult] = useState<UseScreenScaleResult>({
    scale: 1,
    containerWidth: 0,
    containerHeight: 0,
    isReady: false,
    scaledWidth: SCREEN_DESIGN_SIZE.WIDTH,
    scaledHeight: SCREEN_DESIGN_SIZE.HEIGHT,
  });

  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const calculateScale = useCallback(() => {
    const container = containerRef.current;
    if (!container) return;

    const containerWidth = container.clientWidth - padding * 2;
    const containerHeight = container.clientHeight - padding * 2;

    if (containerWidth <= 0 || containerHeight <= 0) return;

    // Calculate scale using "contain" logic (fit entirely within container)
    const scaleX = containerWidth / targetWidth;
    const scaleY = containerHeight / targetHeight;
    const rawScale = Math.min(scaleX, scaleY);

    // Clamp to min/max bounds
    const scale = Math.max(minScale, Math.min(maxScale, rawScale));

    setResult({
      scale,
      containerWidth,
      containerHeight,
      isReady: true,
      scaledWidth: targetWidth * scale,
      scaledHeight: targetHeight * scale,
    });
  }, [containerRef, padding, minScale, maxScale, targetWidth, targetHeight]);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const handleResize = () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
      timeoutRef.current = setTimeout(calculateScale, debounce);
    };

    // Initial calculation
    calculateScale();

    // Set up ResizeObserver for responsive updates
    const observer = new ResizeObserver(handleResize);
    observer.observe(container);

    return () => {
      observer.disconnect();
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, [containerRef, calculateScale, debounce]);

  return result;
}
